"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getSceneLayout = getSceneLayout;
exports.isLastPresenter = isLastPresenter;
exports.isLayout = isLayout;
exports.isMediaSolo = isMediaSolo;
exports.isMediaSource = isMediaSource;
exports.isMultiUserLayout = isMultiUserLayout;
exports.isPresenter = isPresenter;
exports.isPresenterSolo = isPresenterSolo;
exports.isScreenShare = isScreenShare;
exports.isScreenShareSolo = isScreenShareSolo;
exports.isSingleLayout = isSingleLayout;
exports.selectSceneLayout = selectSceneLayout;
var _SceneLayouts = require("@studio/constants/SceneLayouts");
var _Sources = require("@studio/constants/Sources");
/**
 * @param {{}} sceneLayout
 */
function isMultiUserLayout(sceneLayout) {
  return [_SceneLayouts.GRID, _SceneLayouts.SIDE_BY_SIDE, _SceneLayouts.FEATURED_MEDIA, _SceneLayouts.FEATURED_SCREEN, _SceneLayouts.FEATURED_PRESENTER].some(function (layout) {
    return layout.title === sceneLayout.title;
  });
}

/**
 * Checks if layout is SINGLE
 * @param {{}} sceneLayout
 */
function isSingleLayout(sceneLayout) {
  return sceneLayout.title === _SceneLayouts.SINGLE.title;
}

/**
 * Checks if source is a media source.
 * This should not include AUDIO_FILE since they should NOT change layout.
 * @param {{}} source
 * @returns Boolean
 */
function isMediaSource(source) {
  var sourceType = source.type;
  return [_Sources.IMAGE_FILE, _Sources.PDF_FILE, _Sources.VIDEO_FILE, _Sources.MEDIA].includes(sourceType);
}

/**
 * Checks if source is screen share
 * @param {{}} source
 */
function isScreenShare(source) {
  return source.type === _Sources.SCREEN_PRESENTER;
}

/**
 * Checks if source is presenter
 * @param {{}} source
 */
function isPresenter(source) {
  return source.type === _Sources.CAMERA_PRESENTER;
}

/**
 *
 * @param {Array} sources
 * @param {{}} source
 */
var containsSource = function containsSource(sources, source) {
  if (!sources.length) return true;
  return sources.some(function (s) {
    return s._id === source._id;
  });
};

/**
 * @param {{}} sceneLayout
 * @param {Array} sources
 */
function isMediaSolo(sceneLayout, sources) {
  return isSingleLayout(sceneLayout) && sources.some(function (s) {
    return isMediaSource(s);
  });
}

/**
 * @param {{}} sceneLayout
 * @param {Array} sources
 */
function isScreenShareSolo(sceneLayout, sources) {
  return isSingleLayout(sceneLayout) && sources.some(function (s) {
    return isScreenShare(s);
  });
}

/**
 * @param {{}} sceneLayout
 * @param {Array} sources
 */
function isPresenterSolo(sceneLayout, sources) {
  return isSingleLayout(sceneLayout) && sources.some(function (s) {
    return isPresenter(s);
  });
}

/**
 * @param {{}} sceneLayout
 * @param {Array} sources
 */
function isLastPresenter(sources) {
  return sources.filter(function (s) {
    return s.type === _Sources.CAMERA_PRESENTER;
  }).length === 1;
}

/** Check if layout is a valid layout */
function isLayout(layout, compare) {
  return (layout === null || layout === void 0 ? void 0 : layout.title) === (compare === null || compare === void 0 ? void 0 : compare.title) || (layout === null || layout === void 0 ? void 0 : layout.id) === (compare === null || compare === void 0 ? void 0 : compare.id);
}

/**
 * Decide the sceneLayout based on user activity
 * @param {{}} sceneLayout
 * @param {{}} source
 * @param {Array} sources
 */
function selectSceneLayout(sceneLayout, source, sources) {
  // In a featured media/screen layout and we toggle a media source - switch to single layout
  if (isMultiUserLayout(sceneLayout) && containsSource(sources, source)) {
    // Switch to grid/single
    if (isMediaSource(source) || isScreenShare(source)) {
      return sources.length > 2 ? _SceneLayouts.GRID : _SceneLayouts.SINGLE;
    }

    // If last presenter switch to single source layout otherwise use the same layout
    if (isPresenter(source)) {
      return isLastPresenter(sources, source) ? _SceneLayouts.SINGLE : sceneLayout;
    }
  }

  // In a featured media/screen layout and we add a screen-share/media source - switch to featured media/screen layout
  if (isMultiUserLayout(sceneLayout) && !containsSource(sources, source)) {
    // Switch to featured media
    if (isMediaSource(source)) {
      return _SceneLayouts.FEATURED_MEDIA;
    }

    // Switch to featured screen
    if (isScreenShare(source)) {
      return _SceneLayouts.FEATURED_SCREEN;
    }
  }

  // If presenter in SOLO layout
  if (isPresenterSolo(sceneLayout, sources)) {
    // If add live media source - switch to featured media
    if (isMediaSource(source)) return _SceneLayouts.FEATURED_MEDIA;

    // if add live screen share - switch to featured screen
    if (isScreenShare(source)) return _SceneLayouts.FEATURED_SCREEN;

    // If add another presenter - switch to grid
    if (isPresenter(source) & !containsSource(sources, source)) return _SceneLayouts.GRID;
  }

  // If a media source in program and source is presenter - switch to featured media layout
  if (isMediaSolo(sceneLayout, sources) && isPresenter(source)) {
    return _SceneLayouts.FEATURED_MEDIA;
  }
  if (isScreenShareSolo(sceneLayout, sources) && isPresenter(source)) {
    return _SceneLayouts.FEATURED_SCREEN;
  }
  return sceneLayout;
}

/**
 * Returns the layout for the scene
 * @param {Object} scene
 * @return {{id: *, title: *}}
 */
function getSceneLayout(scene) {
  var sceneLayout = {
    id: scene.layout,
    title: scene.title
  };
  // Change featured layout to grid when no featured source exist to avoid assigning placeholder default
  var isFeatured = isLayout(sceneLayout, _SceneLayouts.FEATURED_SCREEN) || isLayout(sceneLayout, _SceneLayouts.FEATURED_MEDIA) || isLayout(sceneLayout, _SceneLayouts.FEATURED_PRESENTER);
  if (isFeatured) {
    var hasFeaturedSource = scene.layers.some(function (_ref) {
      var layer = _ref.layer,
        source = _ref.source;
      var isAFeaturedFile = [_Sources.IMAGE_FILE, _Sources.PDF_FILE, _Sources.VIDEO_FILE, _Sources.MEDIA, _Sources.SCREEN_PRESENTER].includes(source.type);
      return isAFeaturedFile && (layer.isContent === undefined || layer.isContent === true);
    });
    if (!hasFeaturedSource) {
      sceneLayout = _SceneLayouts.GRID;
      scene.layout = sceneLayout.id;
      scene.title = sceneLayout.title;
    }
  }
  return sceneLayout;
}